import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import type { AssistantScreeningData } from '@cumpli/shared';
import { Pill, cn, type Tone } from '../../ui';
import { usePrefersReducedMotion } from '../../../lib/motion';
import { CardHeader, CardShell } from './shell';
import { MaybeRedacted, isRedacted } from './redacted';

const STATUS_META: Record<'hit' | 'review' | 'clear', { tone: Tone; label: string }> = {
  clear: { tone: 'green', label: 'Sin coincidencias' },
  review: { tone: 'amber', label: 'Posible coincidencia' },
  hit: { tone: 'red', label: 'Coincidencia en listas' },
};

const TIER_META: Record<'hit' | 'review', { tone: Tone; label: string }> = {
  hit: { tone: 'red', label: 'Coincidencia' },
  review: { tone: 'amber', label: 'Revisar' },
};

/** The lists cotejadas, shown ticking in during a just-run screening. */
const SCREENED_LISTS = ['OFAC SDN', 'ONU', 'Unión Europea', 'Reino Unido', 'Canadá'] as const;
const SCAN_MS = 1200;

/** The "scanning lists" reveal — plays ONLY on a just-run card (never settled/restored). */
function ScanningLists() {
  return (
    <div className="relative mt-2.5 overflow-hidden rounded-lg">
      <div className="space-y-1.5">
        {SCREENED_LISTS.map((name, i) => (
          <div
            key={name}
            className="flex items-center gap-2 rounded-lg bg-canvas px-2.5 py-1.5 motion-safe:animate-slide-up"
            style={{ animationDelay: `${i * 180}ms`, animationFillMode: 'both' }}
          >
            <span className="grid h-4 w-4 place-items-center rounded-full bg-primary/10 text-primary-dark">
              <Check className="h-2.5 w-2.5" strokeWidth={3} />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-soft">{name}</span>
          </div>
        ))}
      </div>
      {/* Soft maroon scan-line sweeping down over the lists while they cotejan. */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-primary/15 to-transparent motion-safe:animate-scan-sweep motion-reduce:hidden" />
    </div>
  );
}

/**
 * `screen_name` — the result of cotejar a name against the sanctions lists.
 * The header pill states the overall verdict; each match row shows the list,
 * the matched name, its score and tier. This is the SETTLED outcome — no fake
 * per-list spinners. The one exception is a just-run card in demo mode
 * (`justRan`): it plays a brief "scanning lists" sweep before the verdict pops.
 */
export function ScreeningCard({
  data,
  justRan = false,
}: {
  data: AssistantScreeningData;
  justRan?: boolean | undefined;
}) {
  const reduced = usePrefersReducedMotion();
  const animating = justRan && !reduced;
  const [scanning, setScanning] = useState<boolean>(animating);

  useEffect(() => {
    if (!animating) {
      setScanning(false);
      return;
    }
    setScanning(true);
    const id = setTimeout(() => setScanning(false), SCAN_MS);
    return () => clearTimeout(id);
  }, [animating]);

  const statusMeta = isRedacted(data.status) ? null : STATUS_META[data.status];
  return (
    <CardShell>
      <CardHeader title={<MaybeRedacted value={data.name} />} label="Listas" />

      {scanning ? (
        <ScanningLists />
      ) : (
        <>
          <div className={cn('mt-2.5', animating && 'motion-safe:animate-pop')}>
            <Pill tone={statusMeta?.tone ?? 'slate'}>
              <MaybeRedacted value={data.status} render={(s) => STATUS_META[s].label} />
            </Pill>
          </div>

          {data.matches.length > 0 && (
            <div className="mt-2.5 space-y-1.5">
              {data.matches.map((mt, i) => (
                <div key={i} className="rounded-lg bg-canvas px-2.5 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                      <MaybeRedacted value={mt.listName} />
                    </span>
                    <Pill tone={isRedacted(mt.tier) ? 'slate' : TIER_META[mt.tier].tone}>
                      <MaybeRedacted value={mt.tier} render={(t) => TIER_META[t].label} />
                    </Pill>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                      <MaybeRedacted value={mt.matchedName} />
                    </span>
                    <span className="shrink-0 font-mono text-[11px] text-ink-soft">
                      <MaybeRedacted value={mt.score} render={(s) => `${s}%`} />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </CardShell>
  );
}
